import EmptyCartImage from '@assets/icons/empty-cart-image.svg';
import FilterIcon from '@assets/icons/filter.svg';
import i18n from '@/utils/i18n';
import React, { useMemo, useState } from 'react';
import SearchIcon from '@assets/icons/search.svg';
import { Action } from '@/components/navigation-header-content';
import { ActivityIndicator } from '@/components/activity-indicator';
import { AD_VIEWABILITY_CONFIG, useAdsContext } from '@/contexts/ads-context';
import { AdImpressionSource } from '@/contexts/ads-context-types';
import { AuthStackParams, CatalogStackParams } from '@/navigators/types';
import {
  BASE_SORT_ORDERS,
  GUEST_MODE_SORT_ORDERS,
  useUserContext,
  useUserSettingsContext,
} from '@/contexts/user-context';
import { CATALOG_ROUTES } from '@/navigators/routes';
import { createStyles } from '@/contexts/theme-context';
import { EmptyStateMinimal } from '@/components/empty-state-minimal';
import {
  FilteredCategoryItemGroup,
  ItemListOption,
} from '@/contexts/item-context-types';
import { GTINFilterActionSheet } from '@/components/gtin-filters/gtin-filter-action-sheet';
import { GTINFilterSidePanel } from '@/components/gtin-filters/gtin-filter-side-panel';
import { ItemGroupSelector } from '@/components/item-group-selector';
import { ItemSearchHeaderContainer } from '@/components/orders/item-search-header-container';
import { Props as ListItemProps } from '@/components/list-item';
import { ListViewToggle } from '@/components/list-view-toggle';
import { Props as NavigationHeaderProps } from '@/components/navigation-header';
import { OrderableItemList } from '@/components/orders/orderable-item-list';
import { OrderableItemListItemWithRowQuantity } from '@/components/orders/orderable-item-list-item';
import { SortOrderButton } from '@/components/orders/sort-order-button';
import { SortOrderSectionHeader } from '@/components/orders/sort-order-section-header';
import { SplitFilterContainer } from '@/components/split-filter-container';
import { StackScreenProps } from '@react-navigation/stack';
import { useCatalogCategoryList } from '@/hooks/use-catalog-category-list';
import { useCatalogFilteringEnabled } from '@/hooks/dynamic-config/use-catalog-filtering-enabled';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useItemDetailContext } from '@/contexts/item-detail-context';
import { useItemGroupBadgeFilters } from '@/hooks/use-item-group-badge-filters';
import { useItemGroupCategoryFilters } from '@/hooks/use-item-group-category-filters';
import {
  useLayoutContext,
  useSmallLayoutSizeValue,
} from '@/contexts/layout-context';
import { useMultiOrderGuideButton } from '@/hooks/use-multi-order-guide-button';
import { useNewOrderActionsContext } from '@/contexts/new-order-actions-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useShowListView } from '@/hooks/use-show-list-view';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';
import { View } from 'react-native';
import { ViewOrderButton } from '@/components/partners/view-order-button';

export type Props = StackScreenProps<
  CatalogStackParams & AuthStackParams,
  CATALOG_ROUTES.CategoryList
>;

export const CatalogCategoryListScreen: React.FC<Props> = ({
  navigation,
  route,
}) => {
  const { category } = route.params;
  const { bottom } = useSafeAreaInsets();
  const { debouncedUpdateQuantity } = useNewOrderActionsContext();
  const { isSmallLayout } = useLayoutContext();
  const { variantPackGroupMap } = useVariantPackInfoContext();
  const { catalogSortOrder, setCatalogSortOrder } = useUserSettingsContext();
  const { isGuestMode } = useUserContext();
  const { selectItemListOption } = useItemDetailContext();
  const { onViewableAdItemsChanged } = useAdsContext();
  const [styles, theme] = useStyles();
  const [filterSheetVisible, setFilterSheetVisible] = useState(false);

  const catalogFilteringEnabled = useCatalogFilteringEnabled();
  const itemGroupsEnabled = useDynamicConfigValue('item_groups_enabled');

  const { showListViewToggle, showListView } = useShowListView();

  const {
    shouldShowOrderGuideButton,
    renderOrderGuideButton,
    renderPartitionSelectorSheet,
  } = useMultiOrderGuideButton();

  const [selectedItemGroupCategory, setSelectedItemGroupCategory] =
    useState<FilteredCategoryItemGroup | null>(null);
  const [badgesFilter, setBadgesFilter] = useState<Set<string>>(new Set());

  const {
    listData,
    loading,
    filtersAvailable,
    gtinFilters,
    fetchNextPage,
    fetchMoreOffset,
    subcategoryItemCounts,
  } = useCatalogCategoryList({
    badgesFilter,
    category,
    selectedItemGroupCategory,
  });

  const { badges } = useItemGroupBadgeFilters({
    category,
    items: listData,
  });

  const {
    categoryItemGroupsInSelector,
    categoryItemGroupsInSelectorWithoutAllFilter,
  } = useItemGroupCategoryFilters({
    categoryUUIDsToFilterFrom:
      variantPackGroupMap[category.uuid!]?.directSubgroupUUIDs ?? [],
    excludeExtraCategories: false,
    filterByCategoryType: false,
    subcategoryItemCounts,
  });

  const listItemProps: Partial<ListItemProps<ItemListOption>> = useMemo(() => {
    return {
      onSelect: (i) => {
        selectItemListOption(i, { source: 'category_list' });
      },
      renderMainContent: (option) => {
        const showOrderGuideButtonForItem = shouldShowOrderGuideButton(option);

        return (
          <OrderableItemListItemWithRowQuantity
            isFocused={false}
            onOptionsPress={(i) => {
              selectItemListOption(i, { source: 'category_list' });
            }}
            option={option}
            renderOrderGuideButton={
              showOrderGuideButtonForItem ? renderOrderGuideButton : undefined
            }
            showListView={showListView}
            showSingleOption
            source="category_list"
            updateQuantity={(updateQuantityData) => {
              debouncedUpdateQuantity(updateQuantityData, {
                source: 'catalog',
              });
            }}
          />
        );
      },
    };
  }, [
    debouncedUpdateQuantity,
    renderOrderGuideButton,
    selectItemListOption,
    shouldShowOrderGuideButton,
    showListView,
  ]);

  const itemGroupCategoriesInSideBar =
    itemGroupsEnabled &&
    categoryItemGroupsInSelectorWithoutAllFilter.length > 1;
  const showSideBar = itemGroupCategoriesInSideBar || filtersAvailable;

  const shouldAnimate = isSmallLayout || !showSideBar;

  const baseNavHeaderProps = useSmallLayoutSizeValue<
    Partial<NavigationHeaderProps>
  >(
    {
      animate: shouldAnimate,
      showBackButton: true,
      title: category.name || i18n.t('other'),
    },
    {
      animate: shouldAnimate,
      backButtonLabel: i18n.t('back_to', { name: i18n.t('catalog') }),
      showBackButton: false,
      title: category.name || i18n.t('other'),
    },
  );

  // With local GTIN filters, it's possible that there are no items in list data
  // but we need the list to render to fetch the next pages from the server
  const renderEmptyState =
    !loading && listData.length === 0 && typeof fetchMoreOffset !== 'number';

  const navigationHeaderProps = useMemo(() => {
    const actions: Action[] = [];
    if (isSmallLayout) {
      if (filtersAvailable || Boolean(badges.length)) {
        actions.push({
          Icon: FilterIcon,
          iconProps: {
            fill: undefined,
            stroke: theme.colors.primary,
          },
          onPress: () => {
            setFilterSheetVisible(true);
          },
          testID: 'filter-icon',
        });
      }
      actions.push({
        Icon: SearchIcon,
        onPress: () => {
          navigation.push(CATALOG_ROUTES.Search, {
            category,
          });
        },
        testID: 'search-icon',
      });
    }
    const sortOrderButtonProps = {
      onChange: setCatalogSortOrder,
      supportedSortOptions: isGuestMode
        ? GUEST_MODE_SORT_ORDERS
        : BASE_SORT_ORDERS,
      value: catalogSortOrder,
    };

    const showItemGroupSelector =
      itemGroupsEnabled && categoryItemGroupsInSelector.length > 1;

    return {
      ...baseNavHeaderProps,
      actions,
      after:
        isSmallLayout && !renderEmptyState ? (
          <SortOrderSectionHeader {...sortOrderButtonProps} />
        ) : null,
      centerTitle: isSmallLayout,
      responsive: true,
      subheader: isSmallLayout ? (
        showItemGroupSelector ? (
          <View style={styles.subheaderContainer}>
            <View style={styles.itemGroupsContainer}>
              <ItemGroupSelector
                data={categoryItemGroupsInSelector}
                onItemGroupPress={setSelectedItemGroupCategory}
                selectedItemGroup={selectedItemGroupCategory}
                trackingKey="item_group_selector"
              />
            </View>
          </View>
        ) : null
      ) : (
        <View style={styles.sortOrderRow}>
          {showListViewToggle ? <ListViewToggle /> : null}
          <SortOrderButton
            buttonProps={{
              containerStyle: styles.sortButtonContainer,
              style: styles.sortButtonStyle,
            }}
            {...sortOrderButtonProps}
          />
        </View>
      ),
    };
  }, [
    badges,
    baseNavHeaderProps,
    catalogSortOrder,
    category,
    categoryItemGroupsInSelector,
    filtersAvailable,
    isGuestMode,
    isSmallLayout,
    itemGroupsEnabled,
    navigation,
    renderEmptyState,
    selectedItemGroupCategory,
    setCatalogSortOrder,
    showListViewToggle,
    styles.itemGroupsContainer,
    styles.sortButtonContainer,
    styles.sortButtonStyle,
    styles.sortOrderRow,
    styles.subheaderContainer,
    theme.colors.primary,
  ]);

  return (
    <ItemSearchHeaderContainer
      animateNavigationHeader
      category={category}
      includeGTINFilters={catalogFilteringEnabled}
      loading={loading}
      navigationHeaderProps={navigationHeaderProps}
      searchSource="catalog_category_list"
    >
      {(scrollViewProps) => (
        <SplitFilterContainer
          FilterComponent={
            itemGroupsEnabled ? (
              <GTINFilterSidePanel
                badges={badges}
                badgesFilter={badgesFilter}
                gtinFilters={gtinFilters}
                itemGroupCategories={
                  categoryItemGroupsInSelectorWithoutAllFilter
                }
                onItemGroupCategoryPress={setSelectedItemGroupCategory}
                onSelectBadges={setBadgesFilter}
                selectedItemGroupCategory={selectedItemGroupCategory}
              />
            ) : (
              <GTINFilterSidePanel gtinFilters={gtinFilters} />
            )
          }
          filtersVisible={!isSmallLayout && showSideBar}
        >
          {loading ? (
            <View
              style={[
                styles.loadingContainer,
                shouldAnimate ? scrollViewProps.contentContainerStyle : null,
              ]}
            >
              <ActivityIndicator />
            </View>
          ) : (
            <>
              {renderEmptyState ? (
                <EmptyStateMinimal
                  containerStyle={styles.emptyStateContainer}
                  ImageComponent={EmptyCartImage}
                  text={i18n.t('no_search_results')}
                />
              ) : (
                <OrderableItemList
                  listData={listData}
                  listItemProps={listItemProps}
                  onEndReached={fetchNextPage}
                  onViewableItemsChanged={({ viewableItems }) =>
                    onViewableAdItemsChanged(
                      viewableItems,
                      AdImpressionSource.Catalog,
                    )
                  }
                  scrollViewProps={{
                    ...scrollViewProps,
                    contentContainerStyle: shouldAnimate
                      ? scrollViewProps.contentContainerStyle
                      : null,
                  }}
                  showListView={showListView}
                  viewabilityConfig={AD_VIEWABILITY_CONFIG}
                />
              )}
            </>
          )}
          <ViewOrderButton style={{ marginBottom: bottom }} />
          {renderPartitionSelectorSheet()}
          {isSmallLayout && filterSheetVisible ? (
            <GTINFilterActionSheet
              badges={badges}
              badgesFilter={badgesFilter}
              gtinFilters={gtinFilters}
              onClose={() => setFilterSheetVisible(false)}
              onSelectBadges={setBadgesFilter}
            />
          ) : null}
        </SplitFilterContainer>
      )}
    </ItemSearchHeaderContainer>
  );
};

const useStyles = createStyles((theme) => ({
  emptyStateContainer: {
    backgroundColor: theme.colors.transparent,
  },
  itemGroupsContainer: {
    flex: 1,
    justifyContent: 'flex-start',
  },
  loadingContainer: {
    flex: 1,
    marginTop: 24,
  },
  sortButtonContainer: {
    borderLeftColor: theme.colors.stroke,
    borderLeftWidth: 1,
  },
  sortButtonStyle: {
    paddingHorizontal: 16,
  },
  sortOrderRow: {
    alignItems: 'center',
    borderTopColor: theme.colors.stroke,
    borderTopWidth: 1,
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  subheaderContainer: {
    borderTopColor: theme.colors.stroke,
    borderTopWidth: 1,
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
}));
